import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useState } from "react";
import { Field, Formik } from "formik";
import AddButton, { DraftButton } from "../../buttons/button";
import successToast from "../../helper-components/success-toast";
import errorToast from "../../helper-components/error-toast";
import { useDispatch } from "react-redux";
import {
  customNumberingSeriesAddAction,
  customNumberingSeriesListAction,
  documentNumberingSeriesAddAction,
  documentNumberingSeriesListAction,
} from "../../../redux/actions/settings-action";
import { formatString } from "../../helper-components/helper-function";
import { resetOptions } from "../../helper-components/data";
import LoadingOverlay from "../../helper-components/overlay-loading";
import { toInteger } from "lodash";

const DocumentNumberingEditModal = ({
  isModalOpen,
  setIsModalOpen,
  docId,
  setReload,
  title = "Invoice",
  doc_type,
}) => {
  const tableHeading = [
    "Name",
    "Prefix",
    "Suffix",
    "First No.",
    "Last No.",
    "Increment",
    "Include Year",
    "Include Month",
    "Include Date",
    "Separotor",
    "Leading Spaces",
    "Series FYI",
    ...(doc_type === "custom_numbering_series" ? ["Reset Type"] : []),
    "Default",
  ];

  const dispatch = useDispatch();
  const [initialData, setDocumentSeriesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previousDefaultIndex, setPreviousDefaultIndex] = useState(-1);

  useEffect(() => {
    setLoading(true);
    let actionType = documentNumberingSeriesListAction;
    if (doc_type === "custom_numbering_series") {
      actionType = customNumberingSeriesListAction;
    }
    dispatch(
      actionType({ transaction_doc_id: docId }, onFetchSuccess, onFetchError)
    );
  }, [docId]);

  const onFetchSuccess = (data) => {
    setDocumentSeriesData(data?.data);
    setLoading(false);
  };

  const onFetchError = (data) => {
    setDocumentSeriesData([]);
    setLoading(false);
  };

  const handleFieldChange = (index, field, value, setFieldValue) => {
    setFieldValue(`${index}.${field}`, value);
  };

  const handleDefaultChange = (selectedIndex, setFieldValue, values) => {
    // Store the previous default index
    setPreviousDefaultIndex(selectedIndex);

    // Uncheck all existing data series_default
    values.data.forEach((_, index) => {
      setFieldValue(`data.${index}.series_default`, index === selectedIndex);
    });

    // Uncheck newLine series_default
    setFieldValue("newLine.series_default", false);
  };

  const handleNewLineDefaultChange = (setFieldValue, values) => {
    // Store the currently selected default index before unchecking
    const currentDefaultIndex = values.data.findIndex(
      (item) => item.series_default === true
    );
    if (currentDefaultIndex !== -1) {
      setPreviousDefaultIndex(currentDefaultIndex);
    }

    // Uncheck all existing data series_default
    values.data.forEach((_, index) => {
      setFieldValue(`data.${index}.series_default`, false);
    });

    // Check newLine series_default
    setFieldValue("newLine.series_default", true);
  };

  const initialValues = {
    data: initialData,
    newLine: {
      series_name: "",
      series_prefix: "",
      series_suffix: "",
      series_first_digit: null,
      series_last_digit: null,
      series_separator: "",
      series_default: false,
      series_increment: null,
      series_include_year: false,
      series_include_month: false,
      series_include_date: false,
      series_leading_spaces: null,
      transaction_doc_id: docId,
      series_fyi_year: null,
      series_id: "",
      ...(doc_type === "custom_numbering_series" && { reset_type: "" }),
    },
  };

  const onSubmit = (values, { setSubmitting }) => {
    let actionType = documentNumberingSeriesAddAction;
    if (doc_type === "custom_numbering_series") {
      actionType = customNumberingSeriesAddAction;
    }
    const hasNewLineData = Object.entries(values.newLine).some(
      ([key, value]) => {
        if (typeof value === "boolean" || key === "transaction_doc_id") {
          return false;
        }

        if (typeof value === "string") {
          return value.trim() !== "";
        }

        if (typeof value === "number") {
          return value !== null && value !== undefined;
        }
      }
    );

    // Create a copy of the data to modify
    let dataToSubmit = [...values.data];

    // Handle the case where hasNewLineData is false but newLine series_default is true
    if (!hasNewLineData && values.newLine.series_default) {
      // Restore the previously selected default item
      if (
        previousDefaultIndex !== -1 &&
        previousDefaultIndex < dataToSubmit.length
      ) {
        dataToSubmit = dataToSubmit.map((item, index) => ({
          ...item,
          series_default: index === previousDefaultIndex,
        }));
      } else if (dataToSubmit.length > 0) {
        // If no valid previous default found, set the first item as default
        dataToSubmit = dataToSubmit.map((item, index) => ({
          ...item,
          series_default: index === 0,
        }));
      }

      // Clear the newLine series_default since we're not including newLine data
      values.newLine.series_default = false;
    }

    // If hasNewLineData is true, include the newLine in submission
    const finalDataToSubmit = hasNewLineData
      ? [...dataToSubmit, values.newLine]
      : dataToSubmit;
    let canSubmit = true;
    for (const i of finalDataToSubmit) {
      const first = toInteger(i?.series_first_digit);
      const last = toInteger(i?.series_last_digit);
      const increment = i?.series_increment;
      if (i?.series_first_digit && i?.series_last_digit && first > last) {
        errorToast("First number should not be greater than last number");
        canSubmit = false;
        break;
      }
      if (i?.series_increment) {
        const positiveIntRegex = /^[1-9][0-9]*$/;
        const isPositiveNumber =
          (typeof increment === "number" && increment > 0) ||
          (typeof increment === "string" && positiveIntRegex.test(increment.trim()));

        if (!isPositiveNumber) {
          errorToast("Series increment should be a positive number");
          canSubmit = false;
          break;
        }
      }
      if (i?.series_first_digit && i?.series_last_digit && i?.series_increment) {
        const firstNum = toInteger(i.series_first_digit);
        const lastNum = toInteger(i.series_last_digit);
        const step = toInteger(i.series_increment);
        const nextNumber = firstNum + step;

        if (nextNumber > lastNum) {
          errorToast(
            "Series increment causes the number to go beyond the last digit range"
          );
          canSubmit = false;
          break;
        }
      }
    }

    if(canSubmit){
      console.log(finalDataToSubmit, "finalDataToSubmit");
      setSubmitting(false);
    }

    dispatch(actionType(finalDataToSubmit, onCreateSuccess, onCreateError));
  };

  const onCreateSuccess = (data) => {
    setReload((prev) => !prev);
    successToast(data?.data?.message);
    setIsModalOpen(false);
  };
  const onCreateError = (data) => {
    errorToast(data?.message);
  };

  return (
    <Transition show={isModalOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={() => setIsModalOpen(false)}
      >
        <div className="flex min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-60" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-4"
          >
            <div className="mx-auto  w-11/12 my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-xl">
              <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                onSubmit={onSubmit}
              >
                {({ setFieldValue, handleSubmit, values, resetForm }) => {
                  return (
                    <>
                      <LoadingOverlay isLoading={loading} />

                      <div className="w-full h-full flex flex-col">
                        <div className="flex flex-col items-start w-full px-6 pt-8">
                          <label className="mx-3 fs_20 fw_600">
                            {formatString(title)}
                          </label>

                          <div
                            style={{ height: "100%" }}
                            className="overflow-auto rounded-xl table-container border border-gray-200"
                          >
                            <table className=" w-full ">
                              <thead className="sticky top-0 bg-[var(--create-table-header-bg)] z-[10]">
                                <tr className="relative h-[50px]">
                                  {tableHeading.map((heading, index) => (
                                    <th
                                      className={`text-start px-[8px] whitespace-nowrap text-[var(--create-table-header-fg)] text-[length:var(--create-table-header-fs)] font-[var(--create-table-header-fw)]`}
                                      key={heading}
                                    >
                                      {heading?.toUpperCase()}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {values.data.map((item, index) => {
                                  const isdisabled=item?.series_default;
                                  return (
                                  <tr key={index}>
                                    <td className="tab-cell text-sm fw_400 text-secondary text-center">
                                      <Field
                                        name={`data.${index}.series_name`}
                                        placeholder="Name"
                                        className={`border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded py-2 w-full ${isdisabled ?"cursor-not-allowed bg-gray-100" : ""}`}
                                        autoFocus
                                        disabled={isdisabled}
                                      />
                                    </td>
                                    <td className="tab-cell text-sm fw_400 text-secondary text-center">
                                      <Field
                                        name={`data.${index}.series_prefix`}
                                        placeholder="Prefix"
                                        className={`border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded py-2 w-full ${isdisabled ?"cursor-not-allowed bg-gray-100" : ""}`}
                                        disabled={isdisabled}
                                      />
                                    </td>
                                    <td className="tab-cell text-sm fw_400 text-secondary text-center">
                                      <Field
                                        name={`data.${index}.series_suffix`}
                                        placeholder="Suffix"
                                        className={`border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-full ${isdisabled ?"cursor-not-allowed bg-gray-100" : ""}`}
                                        disabled={isdisabled}
                                      />
                                    </td>
                                    <td className="tab-cell text-sm fw_400 text-secondary text-center">
                                      <Field
                                        name={`data.${index}.series_first_digit`}
                                        placeholder="First num"
                                        type="number"
                                        className={`border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-full ${isdisabled ?"cursor-not-allowed bg-gray-100" : ""}`}
                                        disabled={isdisabled}
                                      />
                                    </td>
                                    <td className="tab-cell text-sm fw_400 text-secondary text-center">
                                      <Field
                                        name={`data.${index}.series_last_digit`}
                                        placeholder="Last num"
                                        type="number"
                                        className={`border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-full ${isdisabled ?"cursor-not-allowed bg-gray-100" : ""}`}
                                        disabled={isdisabled}
                                      />
                                    </td>
                                    <td className="tab-cell text-sm fw_400 text-secondary text-center">
                                      <Field name={`data.${index}.series_increment`}>
                                        {({ field, form }) => {
                                          const handleChange = (e) => {
                                            const value = e.target.value;
                                            const onlyPositive = /^[1-9][0-9]*$/;
                                            if (value === "" || onlyPositive.test(value)) {
                                              form.setFieldValue(field.name, value);
                                            } else {
                                              errorToast("only positive numbers allowed");
                                              form.setFieldValue(field.name, "");
                                            }
                                          };
                                          return (
                                            <input
                                              {...field}
                                              placeholder="Series increment"
                                              className={`border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-full ${isdisabled ?"cursor-not-allowed bg-gray-100" : ""}`}
                                              disabled={isdisabled}
                                              onChange={handleChange}
                                              inputMode="numeric"
                                            />
                                          );
                                        }}
                                      </Field>
                                    </td>
                                    <td className="tab-cell text-sm fw_400 text-secondary text-center">
                                      <Field
                                        name={`data.${index}.series_include_year`}
                                        type="checkbox"
                                        className={`border-[1px] focus:outline-none h-[18px] w-[18px] rounded-[6px] px-1 py-2 ${isdisabled ?"cursor-not-allowed bg-gray-100" : ""}`}
                                        disabled={isdisabled}
                                      />
                                    </td>
                                    <td className="tab-cell text-sm fw_400 text-secondary text-center">
                                      <Field
                                        name={`data.${index}.series_include_month`}
                                        type="checkbox"
                                        className={`border-[1px] focus:outline-none h-[18px] w-[18px] rounded-[6px] px-1 py-2 ${isdisabled ?"cursor-not-allowed bg-gray-100" : ""}`}
                                        disabled={isdisabled}
                                      />
                                    </td>
                                    <td className="tab-cell text-sm fw_400 text-secondary text-center">
                                      <Field
                                        name={`data.${index}.series_include_date`}
                                        type="checkbox"
                                        className={`border-[1px] focus:outline-none h-[18px] w-[18px] rounded-[6px] px-1 py-2 ${isdisabled ?"cursor-not-allowed bg-gray-100" : ""}`}
                                        disabled={isdisabled}
                                      />
                                    </td>
                                    <td className="tab-cell text-sm fw_400 text-secondary text-center">
                                      {/* <Field
                                        name={`data.${index}.series_separator`}
                                        placeholder="Series separator"
                                        className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-full"
                                      /> */}
                                      <Field name={`data.${index}.series_separator`}>
                                        {({ field, form }) => {
                                          const handleChange = (e) => {
                                            const value = e.target.value;
                                            const valid = /^(?=.*[!@#$%^&*()_+{}\[\]"'\-\/\.,<>`~])[A-Za-z!@#$%^&*()_+{}\[\]"'\-\/\.,<>`~]+$/.test(value);

                                            if (value === "" || valid) {
                                              form.setFieldValue(field.name, value);
                                            } else {
                                              errorToast("at least one special character should be ther is present");
                                              form.setFieldValue(field.name, "");
                                            }
                                          };

                                          return (
                                            <input
                                              {...field}
                                              placeholder="Series separator"
                                              className={`border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-full ${isdisabled ?"cursor-not-allowed bg-gray-100" : ""}`}
                                              disabled={isdisabled}
                                              onChange={handleChange}
                                            />
                                          );
                                        }}
                                      </Field>
                                    </td>
                                    <td className="tab-cell text-sm fw_400 text-secondary text-center">
                                      <Field
                                        name={`data.${index}.series_leading_spaces`}
                                        placeholder="Series space"
                                        type="number"
                                        className={`border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-full ${isdisabled ?"cursor-not-allowed bg-gray-100" : ""}`}
                                        disabled={isdisabled}
                                      />
                                    </td>
                                    <td className="tab-cell text-sm fw_400 text-secondary text-center">
                                      <Field
                                        name={`data.${index}.series_fyi_year`}
                                        placeholder="Series Fyi Year"
                                        className={`border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-full ${isdisabled ?"cursor-not-allowed bg-gray-100" : ""}`}
                                        disabled={isdisabled}
                                      />
                                    </td>
                                    {doc_type === "custom_numbering_series" && (
                                      <td className="tab-cell text-sm fw_400 text-secondary text-center">
                                        <select
                                          value={
                                            values?.data?.[index].reset_type
                                          }
                                          onChange={(e) =>
                                            setFieldValue(
                                              `data.${index}.reset_type`,
                                              e?.target?.value
                                            )
                                          }
                                          className={`border-radius w-[7rem] px-[5px] py-[8px] border border-[var(--list-table-border-color)] rounded-sm bg-white text-sm text-[var(--list-table-row-fg-color)] ${isdisabled ?"cursor-not-allowed bg-gray-100" : ""}`}
                                          disabled={isdisabled}
                                        >
                                          {resetOptions?.map((opt) => (
                                            <option value={opt?.value}>
                                              {opt?.label}
                                            </option>
                                          ))}
                                        </select>
                                      </td>
                                    )}

                                    <td className="tab-cell text-sm fw_400 text-secondary text-center">
                                      <input
                                        type="checkbox"
                                        checked={
                                          values.data[index]?.series_default ||
                                          false
                                        }
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            handleDefaultChange(
                                              index,
                                              setFieldValue,
                                              values
                                            );
                                          } else {
                                            setFieldValue(
                                              `data.${index}.series_default`,
                                              false
                                            );
                                          }
                                        }}
                                        className={`border-[1px] focus:outline-none h-[18px] w-[18px] rounded-[6px] px-1 py-2 ${isdisabled ?"cursor-not-allowed bg-gray-100" : ""}`}
                                        disabled={isdisabled}
                                      />
                                    </td>
                                  </tr>
                                  )
                                })}

                                <tr>
                                  <td
                                    className={`tab-cell text-sm fw_400 text-secondary text-center`}
                                  >
                                    <Field
                                      name="newLine.series_name"
                                      placeholder="Name"
                                      className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded py-2 w-full"
                                      autoFocus
                                    />
                                  </td>
                                  <td
                                    className={`tab-cell text-sm fw_400 text-secondary text-center`}
                                  >
                                    <Field
                                      name="newLine.series_prefix"
                                      placeholder="Prefix"
                                      className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded py-2 w-full"
                                    />
                                  </td>
                                  <td
                                    className={`tab-cell text-sm fw_400 text-secondary text-center`}
                                  >
                                    <Field
                                      name="newLine.series_suffix"
                                      placeholder="Suffix"
                                      className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-full"
                                    />
                                  </td>
                                  <td
                                    className={`tab-cell text-sm fw_400 text-secondary text-center`}
                                  >
                                    <Field
                                      name={`newLine.series_first_digit`}
                                      placeholder="First num"
                                      type="number"
                                      className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-full"
                                    />
                                  </td>
                                  <td
                                    className={`tab-cell text-sm fw_400 text-secondary text-center`}
                                  >
                                    <Field
                                      name={`newLine.series_last_digit`}
                                      placeholder="Last num"
                                      type="number"
                                      className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-full"
                                    />
                                  </td>
                                  <td
                                    className={`tab-cell text-sm fw_400 text-secondary text-center`}
                                  >
                                    <Field
                                      name={`newLine.series_increment`}
                                      placeholder="Series increment"
                                      className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-full"
                                    />
                                  </td>
                                  <td
                                    className={`tab-cell text-sm fw_400 text-secondary text-center`}
                                  >
                                    <Field
                                      name={`newLine.series_include_year`}
                                      type={"checkbox"}
                                      className="border-[1px] focus:outline-none h-[18px] w-[18px] rounded-[6px] px-1 py-2"
                                    />
                                  </td>
                                  <td
                                    className={`tab-cell text-sm fw_400 text-secondary text-center`}
                                  >
                                    <Field
                                      name={`newLine.series_include_month`}
                                      type={"checkbox"}
                                      className="border-[1px] focus:outline-none h-[18px] w-[18px] rounded-[6px] px-1 py-2"
                                    />
                                  </td>
                                  <td
                                    className={`tab-cell text-sm fw_400 text-secondary text-center`}
                                  >
                                    <Field
                                      name={`newLine.series_include_date`}
                                      type={"checkbox"}
                                      className="border-[1px] focus:outline-none h-[18px] w-[18px] rounded-[6px] px-1 py-2"
                                    />
                                  </td>
                                  <td
                                    className={`tab-cell text-sm fw_400 text-secondary text-center`}
                                  >
                                    <Field
                                      name={`newLine.series_separator`}
                                      placeholder="Series separator"
                                      className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-full"
                                    />
                                  </td>
                                  <td
                                    className={`tab-cell text-sm fw_400 text-secondary text-center`}
                                  >
                                    <Field
                                      name={`newLine.series_leading_spaces`}
                                      placeholder="Series space"
                                      type="number"
                                      className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-full"
                                    />
                                  </td>
                                  <td
                                    className={`tab-cell text-sm fw_400 text-secondary text-center`}
                                  >
                                    <Field
                                      name={`newLine.series_fyi_year`}
                                      placeholder="Series Fyi Year"
                                      type=""
                                      className="border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2 w-full"
                                    />
                                  </td>

                                  {doc_type === "custom_numbering_series" && (
                                    <td
                                      className={`border-b border-[var(--list-table-border-color)] px-[2px] text-left text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap max-w-[7rem]`}
                                    >
                                      <select
                                        value={values?.newLine.reset_type}
                                        onChange={(e) =>
                                          setFieldValue(
                                            `newLine.reset_type`,
                                            e?.target?.value
                                          )
                                        }
                                        className="border-radius w-[7rem] px-[5px] py-[8px] border border-[var(--list-table-border-color)] rounded-sm bg-white text-sm text-[var(--list-table-row-fg-color)]"
                                      >
                                        {resetOptions?.map((opt) => (
                                          <option value={opt?.value}>
                                            {opt?.label}
                                          </option>
                                        ))}
                                      </select>
                                    </td>
                                  )}

                                  <td
                                    className={`tab-cell text-sm fw_400 text-secondary text-center`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={
                                        values.newLine?.series_default || false
                                      }
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          handleNewLineDefaultChange(
                                            setFieldValue,
                                            values
                                          );
                                        } else {
                                          setFieldValue(
                                            "newLine.series_default",
                                            false
                                          );
                                        }
                                      }}
                                      className="border-[1px] focus:outline-none h-[18px] w-[18px] rounded-[6px] px-1 py-2"
                                    />
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="px-6 flex  justify-between py-5 invoice-bottom-container mt-auto">
                          <DraftButton
                            onclkFn={() => {
                              setIsModalOpen(false);
                              resetForm();
                            }}
                            text="Cancel"
                          />
                          <AddButton
                            isDisabled={false}
                            onclkFn={handleSubmit}
                            type="submit"
                            className="bg-primary text-white px-8 py-2 rounded flex justify-center fw_500 text-sm"
                            text="Save"
                            prefixIcon={false}
                          />
                        </div>
                      </div>
                    </>
                  );
                }}
              </Formik>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DocumentNumberingEditModal;
